import { IconFormNames } from './icon-names';
import { ID } from './id';
import { Reminder } from './reminder';
import { Roles } from './roles';
import { TaskAction } from './task-action';
import { Timestamp } from './timestamp';

export enum TaskType {
  Task = 'todo',
  Repeating = 'repeating',
  Habit = 'habit',
}

export enum DueDisplayOptions {
  Past = 'past',
  Today = 'today',
  Future = 'future',
}

export enum TaskPriorityOptions {
  None = 'none',
  High = 'high',
  Medium = 'medium',
  Low = 'low',
}

export const noPriorityOption = 'none';

export type TaskBase = {
  id: ID;
  type: TaskType;
  action?: TaskAction | null;
  roles: Roles;
  name: string;
  /** @deprecated Nothing reads it — see `endStrategy.deadline` / `schedules[].startDate`. Still written on create, filled on read. */
  startDate: Timestamp;
  description?: string | null;
  iconName?: IconFormNames | null;
  goalIds: ID[];
  lifeAreaIds: ID[];
  /**
   * @deprecated Read `goalIds` instead (via `linkIdsOf`). Written on create and on every
   * link edit as `goalIds[0]` so older clients keep resolving their link; read only as the
   * fallback when `goalIds` is absent or empty.
   */
  goalId: ID | null;
  /**
   * @deprecated Read `lifeAreaIds` instead (via `linkIdsOf`). Written on create and on
   * every link edit as `lifeAreaIds[0]` so older clients keep resolving their link; read
   * only as the fallback when `lifeAreaIds` is absent or empty.
   */
  lifeAreaId: ID | null;
  // Deprecated: superseded by `reminders`. Kept for legacy data + backend back-compat.
  reminderTime: number | null;
  reminders?: Reminder[] | null;
  parentIds: ID[] | null;
  childIds?: ID[] | null;
  subtaskOrder?: ID[] | null;
  createdAt: Timestamp;
  archivedAt: Timestamp | null;
  frozenAt: Timestamp | null;
  priority?: TaskPriorityOptions | null;
  endStrategy: {
    deadline: Timestamp | null;
    completionCount: number | null;
  } | null;
  deadlineDeadline?: Timestamp | null;
  completedAt: Timestamp | null;
  deletedAt?: Timestamp | null;
  updatedAt: Timestamp | null;
  // only present on tasks made with the entry goal
  excludedFromLimits?: boolean | null;
  // How often a todo's planned day moved later; only the backend (onWriteTasks) writes it, absent means never.
  postponeCount?: number;
};

export type NewTaskBase = {
  createdAt: Timestamp;
  name: string;
  description?: string | null;
  action?: TaskAction | null;
  iconName?: IconFormNames | null;
  goalIds?: ID[];
  lifeAreaIds?: ID[];
  // Deprecated: superseded by `goalIds`/`lifeAreaIds`. The converter seeds it from the array's first entry.
  goalId: ID | null;
  lifeAreaId: ID | null;
  reminderTime: number | null;
  reminders?: Reminder[] | null;
  /** @deprecated Nothing reads it — see `endStrategy.deadline` / `schedules[].startDate`. Still written on create, filled on read. */
  startDate: Timestamp | null;
  parentIds: ID[] | null;
  priority: TaskPriorityOptions | null;
  endStrategy: {
    deadline: Timestamp | null;
    completionCount: number | null;
  } | null;
  // forward-compat: not applicable — write-only creation payload, never read back through a converter
  deadlineDeadline?: Timestamp | null;
};
